import {
  Badge,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@veritrellis/ui";
import { AlertCircle, ChevronRight, ClipboardList, ListChecks, Shield } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";
import { CopyButton } from "../components/CopyButton.js";
import { apiFetch } from "../lib/api.js";
import { usePersistedAdminEnvironment } from "../hooks/usePersistedAdminEnvironment.js";
import { useCurrentWorkspace, useWorkspaceId } from "../hooks/useWorkspaceId.js";
import { envLabel, formatRequestStatus, statusBadgeVariant } from "../lib/requestPresentation.js";

type SummaryData = {
  pending_approvals: number;
  allowed_today: number;
  denied_today: number;
};

type RequestRow = {
  id: string;
  action_type: string;
  status: string;
  environment: string;
  created_at: string;
};

type StatCardProps = {
  label: string;
  value: number | string;
  description: string;
  accent: string;
  linkTo?: string;
  linkLabel?: string;
  loading?: boolean;
  children?: React.ReactNode;
};

const StatCard = ({
  label,
  value,
  description,
  accent,
  linkTo,
  linkLabel,
  loading = false,
  children
}: StatCardProps) => (
  <article className="admin-stat-card">
    <div
      className="admin-stat-card-body"
      style={{ "--stat-accent": accent } as React.CSSProperties}
    >
      <p className="admin-stat-card-label">{label}</p>
      {loading ? (
        <Skeleton style={{ height: 36, width: 64, marginTop: 8 }} />
      ) : (
        <p className="admin-stat-card-value">{value}</p>
      )}
      <p className="admin-stat-card-desc">{description}</p>
      {children}
    </div>
    {linkTo != null ? (
      <div className="admin-stat-card-footer">
        <Link to={linkTo} className="admin-stat-card-link">
          {linkLabel ?? "View all"}
          <ChevronRight style={{ width: 14, height: 14 }} />
        </Link>
      </div>
    ) : null}
  </article>
);

export const DashboardPage = () => {
  const { workspaceId, isLoading: wsLoading } = useWorkspaceId();
  const current = useCurrentWorkspace();
  const [env] = usePersistedAdminEnvironment();

  const summary = useQuery({
    queryKey: ["summary", workspaceId, env],
    enabled: workspaceId !== null,
    queryFn: async () => {
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/summary?environment=${env}`);
      if (!res.ok) {
        throw new Error("summary_failed");
      }
      return (await res.json()) as SummaryData;
    }
  });

  const recentRequests = useQuery({
    queryKey: ["recent-requests", workspaceId, env],
    enabled: workspaceId !== null,
    queryFn: async () => {
      const res = await apiFetch(
        `/admin/workspaces/${workspaceId}/requests?environment=${env}&limit=8`
      );
      if (!res.ok) {
        return { requests: [] as RequestRow[] };
      }
      return (await res.json()) as { requests: RequestRow[] };
    }
  });

  if (wsLoading) {
    return (
      <div className="admin-dashboard">
        <Skeleton style={{ height: 32, width: 200 }} />
        <div className="admin-dashboard-stats">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} style={{ height: 136, borderRadius: 6 }} />
          ))}
        </div>
      </div>
    );
  }
  if (workspaceId === null) {
    return <Navigate to="/onboarding" replace />;
  }

  const data = summary.data;
  const allowed = data?.allowed_today ?? 0;
  const denied = data?.denied_today ?? 0;
  const pending = data?.pending_approvals ?? 0;
  const total = allowed + denied;
  const allowPct = total > 0 ? Math.round((allowed / total) * 100) : 0;

  return (
    <div className="admin-dashboard">
      {pending > 0 ? (
        <div className="admin-dashboard-alert" role="status">
          <p className="admin-dashboard-alert-text">
            {pending} {pending === 1 ? "approval" : "approvals"} awaiting review in {envLabel(env)}
          </p>
          <Button asChild size="sm" variant="outline">
            <Link to="/approvals">Review approvals</Link>
          </Button>
        </div>
      ) : null}

      <section className="admin-dashboard-stats" aria-label="Today's metrics">
        <StatCard
          label="Pending approvals"
          value={pending}
          description="Awaiting human decision"
          accent="#f59e0b"
          linkTo="/approvals"
          linkLabel="Review queue"
          loading={summary.isLoading}
        />
        <StatCard
          label="Allowed today"
          value={allowed}
          description="Permits issued or auto-allowed"
          accent="#14b8a6"
          linkTo="/requests"
          linkLabel="View requests"
          loading={summary.isLoading}
        />
        <StatCard
          label="Denied today"
          value={denied}
          description="Blocked by policy"
          accent="#ef4444"
          linkTo="/requests"
          linkLabel="View requests"
          loading={summary.isLoading}
        />
        <StatCard
          label="Allow rate"
          value={total === 0 ? "—" : `${allowPct}%`}
          description={total === 0 ? "No decisions yet today" : `${allowed} of ${total} decided`}
          accent="#7950f2"
          loading={summary.isLoading}
        >
          {!summary.isLoading && total > 0 ? (
            <div className="admin-rate-bar" aria-hidden>
              <div className="admin-rate-bar-fill" style={{ width: `${allowPct}%` }} />
            </div>
          ) : null}
        </StatCard>
      </section>

      <div className="admin-dashboard-main">
        <section className="admin-panel" aria-labelledby="recent-requests-heading">
          <div className="admin-panel-header">
            <h2 id="recent-requests-heading" className="admin-panel-title">
              Recent requests
            </h2>
            <Link to="/requests" className="admin-panel-link">
              View all
            </Link>
          </div>
          <div className="admin-panel-body">
            {recentRequests.isLoading ? (
              <div className="admin-panel-body-padded">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} style={{ height: 40, width: "100%", marginBottom: 8 }} />
                ))}
              </div>
            ) : recentRequests.data?.requests.length === 0 ? (
              <div className="admin-empty-state">
                <div className="admin-empty-state-icon">
                  <ClipboardList style={{ width: 22, height: 22 }} />
                </div>
                <p className="admin-empty-state-text">No requests in {envLabel(env)} yet</p>
                <Button asChild variant="secondary" size="sm">
                  <Link to="/policies">Configure policies</Link>
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead style={{ paddingLeft: 20 }}>Request</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead style={{ textAlign: "right", paddingRight: 20 }}>Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentRequests.data?.requests.map((req) => (
                    <TableRow key={req.id}>
                      <TableCell style={{ paddingLeft: 20 }}>
                        <Link to={`/requests/${req.id}`} className="admin-table-row-link">
                          <code style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                            {req.id.slice(0, 8)}…
                          </code>
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link to={`/requests/${req.id}`} className="admin-table-row-link">
                          <span style={{ fontSize: 14, fontWeight: 500 }}>{req.action_type}</span>
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusBadgeVariant(req.status)}>
                          {formatRequestStatus(req.status)}
                        </Badge>
                      </TableCell>
                      <TableCell style={{ textAlign: "right", paddingRight: 20 }}>
                        <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                          {new Date(req.created_at).toLocaleString([], {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </section>

        <aside className="admin-dashboard-aside">
          <section className="admin-panel" aria-labelledby="quick-actions-heading">
            <div className="admin-panel-header">
              <h2 id="quick-actions-heading" className="admin-panel-title">
                Quick actions
              </h2>
            </div>
            <div className="admin-panel-body-padded" style={{ paddingTop: 8, paddingBottom: 8 }}>
              {[
                {
                  label: "Review pending approvals",
                  to: "/approvals",
                  icon: ListChecks,
                  bg: "#fff7ed",
                  color: "#c2410c"
                },
                {
                  label: "Browse all requests",
                  to: "/requests",
                  icon: ClipboardList,
                  bg: "#eff6ff",
                  color: "#1d4ed8"
                },
                {
                  label: "Manage policies",
                  to: "/policies",
                  icon: Shield,
                  bg: "#f3f0ff",
                  color: "#6741d9"
                },
                {
                  label: "Audit log",
                  to: "/audit",
                  icon: AlertCircle,
                  bg: "#f1f3f5",
                  color: "#495057"
                }
              ].map(({ label, to, icon: Icon, bg, color }) => (
                <Link key={to} to={to} className="admin-quick-action">
                  <span className="admin-quick-action-icon" style={{ backgroundColor: bg, color }}>
                    <Icon style={{ width: 16, height: 16 }} />
                  </span>
                  <span className="admin-quick-action-label">{label}</span>
                  <ChevronRight style={{ width: 14, height: 14, opacity: 0.35 }} />
                </Link>
              ))}
            </div>
          </section>

          {current.workspaceId !== null ? (
            <section className="admin-panel" aria-labelledby="workspace-id-heading">
              <div className="admin-panel-header">
                <h2 id="workspace-id-heading" className="admin-panel-title">
                  Workspace ID
                </h2>
              </div>
              <div className="admin-panel-body-padded">
                <p style={{ margin: 0, fontSize: 12, color: "var(--muted-foreground)" }}>
                  Use in SDK config as{" "}
                  <code style={{ fontSize: 11 }}>VERITRELLIS_WORKSPACE_ID</code>
                </p>
                <div className="admin-workspace-id">
                  <span>{current.workspaceId}</span>
                  <CopyButton value={current.workspaceId} />
                </div>
              </div>
            </section>
          ) : null}
        </aside>
      </div>
    </div>
  );
};
